<template>
  <div class="min-h-screen bg-background">
    <div class="container mx-auto px-4 py-8">
      <div class="max-w-md mx-auto">
        <!-- Back Button -->
        <button
          @click="goBack"
          class="mb-6 p-2 text-foreground hover:text-secondary transition-colors flex items-center"
        >
          <svg class="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
          Retour
        </button>

        <div class="bg-card border border-border rounded-lg shadow-md">
          <div class="p-8">
            <div class="text-center mb-8">
              <div class="w-16 h-16 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="h-8 w-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
              </div>
              <h1 class="text-2xl font-bold text-foreground">
                Mon Compte
              </h1>
              <p class="text-muted-foreground">
                Connectez-vous ou créez votre compte
              </p>
            </div>

            <!-- Message d'alerte -->
            <div v-if="message.text" 
                 :class="`mb-6 p-4 rounded-lg border ${
                   message.type === 'success' 
                     ? 'bg-green-50 border-green-200 text-green-800' 
                     : 'bg-red-50 border-red-200 text-red-800'
                 }`">
              <div class="flex items-center">
                <svg :class="`h-5 w-5 mr-2 ${
                  message.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path v-if="message.type === 'success'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
                <span class="text-sm font-medium">{{ message.text }}</span>
              </div>
            </div>

            <!-- Tabs Navigation -->
            <div class="flex justify-between  overflow-x-auto w-full gap-2 mb-6 p-1 bg-muted rounded-lg">
              <button
                @click="navigateTo('login')"
                :class="`py-2 px-4 rounded-md w-full text-sm font-medium transition-colors ${
                  activeTab === 'login' 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`"
              >
                Connexion
              </button>
              <button
                @click="navigateTo('register')"
                :class="`py-2 px-4 rounded-md  w-full text-sm font-medium transition-colors ${
                  activeTab === 'register' 
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`"
              >
                Inscription
              </button>
              <button
                v-if="activeTab === 'reset' || activeTab === 'reset-password'"
                @click="navigateTo('reset')"
                :class="`py-2 px-4 rounded-md w-full text-sm font-medium transition-colors ${
                  (activeTab === 'reset' || activeTab === 'reset-password')
                    ? 'bg-background text-foreground shadow-sm' 
                    : 'text-muted-foreground hover:text-foreground'
                }`"
              >
                Réinitialisation
              </button>
            </div>

            <!-- Login Tab -->
            <div v-if="activeTab === 'login'" class="space-y-4">
              <form @submit.prevent="handleLogin" class="space-y-4">
                <div>
                  <label for="login-email" class="block  text-sm font-medium text-foreground mb-2">Email</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <input
                      id="login-email"
                      type="email"
                      placeholder="votre@email.com"
                      v-model="loginData.email"
                      :class="`w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        loginErrors.email ? 'border-destructive' : 'border-input'
                      }`"
                    />
                  </div>
                  <p v-if="loginErrors.email" class="text-sm text-destructive mt-1">{{ loginErrors.email }}</p>
                </div>

                <div>
                  <label for="login-password" class="block text-sm font-medium text-foreground mb-2">Mot de passe</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                    <input
                      id="login-password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      v-model="loginData.password"
                      :class="`w-full pl-10 pr-10 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        loginErrors.password ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <svg v-if="showPassword" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                  <p v-if="loginErrors.password" class="text-sm text-destructive mt-1">{{ loginErrors.password }}</p>
                </div>

                <div class="flex items-center justify-between">
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="loginData.rememberMe"
                      class="rounded border-input bg-background text-primary focus:ring-primary"
                    />
                    <span class="text-sm text-muted-foreground">Se souvenir de moi</span>
                  </label>
                  
                  <button
                    type="button"
                    @click="navigateTo('reset')"
                    class="text-sm text-secondary hover:underline p-0 h-auto"
                  >
                    Mot de passe oublié?
                  </button>
                </div>

                <button 
                  type="submit" 
                  :disabled="isLoading"
                  class="w-full bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isLoading">Connexion en cours...</span>
                  <span v-else>Se connecter</span>
                </button>
              </form>
            </div>

            <!-- Register Tab -->
            <div v-else-if="activeTab === 'register'" class="space-y-4">
              <form @submit.prevent="handleRegister" class="space-y-4">
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label for="firstName" class="block text-sm font-medium text-foreground mb-2">Prénom</label>
                    <input
                      id="firstName"
                      v-model="registerData.firstName"
                      :class="`w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        registerErrors.firstName ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <p v-if="registerErrors.firstName" class="text-sm text-destructive mt-1">{{ registerErrors.firstName }}</p>
                  </div>
                  
                  <div>
                    <label for="lastName" class="block text-sm font-medium text-foreground mb-2">Nom</label>
                    <input
                      id="lastName"
                      v-model="registerData.lastName"
                      :class="`w-full px-3 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        registerErrors.lastName ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <p v-if="registerErrors.lastName" class="text-sm text-destructive mt-1">{{ registerErrors.lastName }}</p>
                  </div>
                </div>

                <div>
                  <label for="register-email" class="block text-sm font-medium text-foreground mb-2">Email</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <input
                      id="register-email"
                      type="email"
                      placeholder="votre@email.com"
                      v-model="registerData.email"
                      :class="`w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        registerErrors.email ? 'border-destructive' : 'border-input'
                      }`"
                    />
                  </div>
                  <p v-if="registerErrors.email" class="text-sm text-destructive mt-1">{{ registerErrors.email }}</p>
                </div>

                <div>
                  <label for="register-password" class="block text-sm font-medium text-foreground mb-2">Mot de passe</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                    <input
                      id="register-password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      v-model="registerData.password"
                      :class="`w-full pl-10 pr-10 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        registerErrors.password ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <svg v-if="showPassword" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                  <p v-if="registerErrors.password" class="text-sm text-destructive mt-1">{{ registerErrors.password }}</p>
                </div>

                <div>
                  <label for="confirmPassword" class="block text-sm font-medium text-foreground mb-2">Confirmer le mot de passe</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                    <input
                      id="confirmPassword"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      v-model="registerData.confirmPassword"
                      :class="`w-full pl-10 pr-10 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        registerErrors.confirmPassword ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <button
                      type="button"
                      @click="showConfirmPassword = !showConfirmPassword"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <svg v-if="showConfirmPassword" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                  <p v-if="registerErrors.confirmPassword" class="text-sm text-destructive mt-1">{{ registerErrors.confirmPassword }}</p>
                </div>

                <div>
                  <label class="flex items-start space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="registerData.acceptTerms"
                      class="rounded border-input bg-background text-primary focus:ring-primary mt-1"
                    />
                    <span class="text-sm text-muted-foreground">
                      J'accepte les{' '}
                      <router-link to="/terms" class="text-secondary hover:underline">
                        conditions générales
                      </router-link>{' '}
                      et la{' '}
                      <router-link to="/privacy" class="text-secondary hover:underline">
                        politique de confidentialité
                      </router-link>
                    </span>
                  </label>
                  <p v-if="registerErrors.acceptTerms" class="text-sm text-destructive mt-1">{{ registerErrors.acceptTerms }}</p>
                </div>

                <button 
                  type="submit" 
                  :disabled="isLoading"
                  class="w-full bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isLoading">Création en cours...</span>
                  <span v-else>Créer mon compte</span>
                </button>
              </form>
            </div>

            <!-- Reset Password Request Tab -->
            <div v-else-if="activeTab === 'reset' && !resetSent" class="space-y-4">
              <div class="text-center mb-6">
                <h2 class="text-lg font-semibold mb-2">Mot de passe oublié?</h2>
                <p class="text-sm text-muted-foreground">
                  Entrez votre email pour recevoir un lien de réinitialisation
                </p>
              </div>

              <form @submit.prevent="handleResetPassword" class="space-y-4">
                <div>
                  <label for="reset-email" class="block text-sm font-medium text-foreground mb-2">Email</label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <input
                      id="reset-email"
                      type="email"
                      placeholder="votre@email.com"
                      v-model="resetEmail"
                      :class="`w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        resetError ? 'border-destructive' : 'border-input'
                      }`"
                    />
                  </div>
                  <p v-if="resetError" class="text-sm text-destructive mt-1">{{ resetError }}</p>
                </div>

                <button 
                  type="submit" 
                  :disabled="isLoading"
                  class="w-full bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isLoading">Envoi en cours...</span>
                  <span v-else>Envoyer le lien</span>
                </button>

                <button
                  type="button"
                  @click="navigateTo('login')"
                  class="w-full text-secondary hover:underline py-2"
                >
                  Retour à la connexion
                </button>
              </form>
            </div>

            <!-- Reset Password Success Message -->
            <div v-else-if="activeTab === 'reset' && resetSent" class="text-center space-y-4">
              <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <svg class="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-foreground mb-2">
                  Email envoyé !
                </h3>
                <p class="text-sm text-muted-foreground mb-4">
                  Nous avons envoyé un lien de réinitialisation à{' '}
                  <strong>{{ resetEmail }}</strong>
                </p>
                <p class="text-xs text-muted-foreground">
                  Vérifiez vos spams si vous ne le trouvez pas dans votre boîte de réception
                </p>
              </div>
              <button
                @click="navigateTo('login')"
                class="w-full border border-input bg-background hover:bg-accent hover:text-accent-foreground py-2 px-4 rounded-lg transition-colors"
              >
                Retour à la connexion
              </button>
            </div>

            <!-- Reset Password Form Tab -->
            <div v-else-if="activeTab === 'reset-password'" class="space-y-4">
              <div class="text-center mb-6">
                <h2 class="text-lg font-semibold mb-2">Nouveau mot de passe</h2>
                <p class="text-sm text-muted-foreground">
                  Entrez votre nouveau mot de passe
                </p>
              </div>

              <form @submit.prevent="handlePasswordReset" class="space-y-4">
                <div v-if="!resetToken">
                  <label for="reset-password-email" class="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                    </svg>
                    <input
                      id="reset-password-email"
                      type="email"
                      placeholder="votre@email.com"
                      v-model="resetEmail"
                      :class="`w-full pl-10 pr-4 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast border-input`"
                    />
                  </div>
                </div>

                <div>
                  <label for="new-password" class="block text-sm font-medium text-foreground mb-2">
                    Nouveau mot de passe
                  </label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                    <input
                      id="new-password"
                      :type="showPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      v-model="newPassword"
                      :class="`w-full pl-10 pr-10 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        passwordResetErrors.password ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <button
                      type="button"
                      @click="showPassword = !showPassword"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <svg v-if="showPassword" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                  <p v-if="passwordResetErrors.password" class="text-sm text-destructive mt-1">
                    {{ passwordResetErrors.password }}
                  </p>
                </div>

                <div>
                  <label for="confirm-new-password" class="block text-sm font-medium text-foreground mb-2">
                    Confirmer le nouveau mot de passe
                  </label>
                  <div class="relative">
                    <svg class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                    </svg>
                    <input
                      id="confirm-new-password"
                      :type="showConfirmPassword ? 'text' : 'password'"
                      placeholder="••••••••"
                      v-model="confirmNewPassword"
                      :class="`w-full pl-10 pr-10 py-2 border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-fast ${
                        passwordResetErrors.confirmPassword ? 'border-destructive' : 'border-input'
                      }`"
                    />
                    <button
                      type="button"
                      @click="showConfirmPassword = !showConfirmPassword"
                      class="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <svg v-if="showConfirmPassword" class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/>
                      </svg>
                      <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                  </div>
                  <p v-if="passwordResetErrors.confirmPassword" class="text-sm text-destructive mt-1">
                    {{ passwordResetErrors.confirmPassword }}
                  </p>
                </div>

                <button 
                  type="submit" 
                  :disabled="isLoading"
                  class="w-full bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isLoading">Réinitialisation en cours...</span>
                  <span v-else>Réinitialiser le mot de passe</span>
                </button>

                <button
                  type="button"
                  @click="navigateTo('login')"
                  class="w-full text-secondary hover:underline py-2"
                >
                  Retour à la connexion
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'AuthPage',
  
  data() {
    return {
      activeTab: 'login',
      showPassword: false,
      showConfirmPassword: false,
      isLoading: false,
      message: {
        type: '',
        text: ''
      },
      
      // Données pour la réinitialisation
      resetSent: false,
      resetEmail: '',
      resetError: '',
      resetToken: '',
      newPassword: '',
      confirmNewPassword: '',
      passwordResetErrors: {},
      
      // Données de connexion
      loginData: {
        email: '',
        password: '',
        rememberMe: false
      },
      loginErrors: {},
      
      // Données d'inscription
      registerData: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        acceptTerms: false
      },
      registerErrors: {}
    }
  },
  
  mounted() {
    // Vérifier si un token de réinitialisation est présent dans l'URL
    const token = this.$route.query.token;
    const tab = this.$route.query.tab;
    
    if (token) {
      this.resetToken = token;
      this.verifyResetToken(token);
      this.activeTab = 'reset-password';
    } else if (tab && ['login', 'register', 'reset', 'reset-password'].includes(tab)) {
      this.activeTab = tab;
    }
    
    // Vérifier si l'utilisateur est déjà connecté
    if (this.$store.state.token) {
      this.$router.push('/account');
    }
    
    // Pré-remplir l'email si rememberMe est activé
    const rememberMe = localStorage.getItem('rememberMe');
    const savedEmail = localStorage.getItem('userEmail');
    
    if (rememberMe === 'true' && savedEmail) {
      this.loginData.email = savedEmail;
      this.loginData.rememberMe = true;
    }
  },
  
  methods: {
    ...mapActions(['login', 'register', 'forgotPassword', 'resetPassword']),
    
    validateLogin() {
      const errors = {}
      
      if (!this.loginData.email.trim()) {
        errors.email = 'Email requis'
      } else if (!/\S+@\S+\.\S+/.test(this.loginData.email)) {
        errors.email = 'Format email invalide'
      }
      
      if (!this.loginData.password.trim()) {
        errors.password = 'Mot de passe requis'
      }
      
      this.loginErrors = errors
      return Object.keys(errors).length === 0
    },
    
    validateRegister() {
      const errors = {}
      
      if (!this.registerData.firstName.trim()) {
        errors.firstName = 'Prénom requis'
      }
      
      if (!this.registerData.lastName.trim()) {
        errors.lastName = 'Nom requis'
      }
      
      if (!this.registerData.email.trim()) {
        errors.email = 'Email requis'
      } else if (!/\S+@\S+\.\S+/.test(this.registerData.email)) {
        errors.email = 'Format email invalide'
      }
      
      if (!this.registerData.password.trim()) {
        errors.password = 'Mot de passe requis'
      } else if (this.registerData.password.length < 6) {
        errors.password = 'Le mot de passe doit contenir au moins 6 caractères'
      }
      
      if (this.registerData.password !== this.registerData.confirmPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas'
      }
      
      if (!this.registerData.acceptTerms) {
        errors.acceptTerms = 'Vous devez accepter les conditions générales'
      }
      
      this.registerErrors = errors
      return Object.keys(errors).length === 0
    },
    
    validateResetForm() {
      this.resetError = ''
      
      if (!this.resetEmail.trim()) {
        this.resetError = 'Email requis'
        return false
      } else if (!/\S+@\S+\.\S+/.test(this.resetEmail)) {
        this.resetError = 'Format email invalide'
        return false
      }
      
      return true
    },
    
    validatePasswordReset() {
      const errors = {}
      
      if (!this.newPassword.trim()) {
        errors.password = 'Nouveau mot de passe requis'
      } else if (this.newPassword.length < 6) {
        errors.password = 'Le mot de passe doit contenir au moins 6 caractères'
      }
      
      if (this.newPassword !== this.confirmNewPassword) {
        errors.confirmPassword = 'Les mots de passe ne correspondent pas'
      }
      
      this.passwordResetErrors = errors
      return Object.keys(errors).length === 0
    },
    
     async handleLogin() {
    if (!this.validateLogin()) return
    
    this.isLoading = true
    this.message = { type: '', text: '' }
    
    try {
      await this.login(this.loginData)
      
      // Récupérer les données de l'utilisateur (incluant le rôle)
      await this.$store.dispatch('fetchCurrentUser');
      
      this.showMessage('success', 'Connexion réussie !')
      
      // Rediriger selon le rôle
      const user = this.$store.state.user;
      let redirectPath = this.$route.query.redirect || '/account';
      
      // Si admin, rediriger vers le dashboard admin
      if (user && (user.role === 'admin' || user.is_admin)) {
        redirectPath = '/admin/dashboard';
      }
      
      setTimeout(() => {
        this.$router.push(redirectPath);
      }, 1500);
      
    } catch (error) {
      this.showMessage('error', error.message || 'Erreur de connexion');
      this.loginErrors = { 
        email: 'Email ou mot de passe incorrect',
        password: 'Email ou mot de passe incorrect'
      };
    } finally {
      this.isLoading = false;
    }
  },
    
    async handleRegister() {
      if (!this.validateRegister()) return
      
      this.isLoading = true
      this.message = { type: '', text: '' }
      
      try {
        await this.register(this.registerData)
        this.showMessage('success', 'Inscription réussie !')
        
        // Rediriger vers la page account
        setTimeout(() => {
          this.$router.push('/account')
        }, 1500)
        
      } catch (error) {
        this.showMessage('error', error.message || "Erreur d'inscription")
        
        // Gérer les erreurs spécifiques du backend
        if (error.errors) {
          Object.keys(error.errors).forEach(key => {
            if (this.registerErrors[key] !== undefined) {
              this.registerErrors[key] = error.errors[key][0]
            }
          })
        }
      } finally {
        this.isLoading = false
      }
    },
    
    async handleResetPassword() {
      if (!this.validateResetForm()) return
      
      this.isLoading = true
      this.message = { type: '', text: '' }
      
      try {
        await this.forgotPassword(this.resetEmail)
        this.resetSent = true
        this.showMessage('success', 'Email de réinitialisation envoyé !')
        
        // Mettre à jour le store avec l'email
        this.$store.commit('setResetEmail', this.resetEmail)
        
      } catch (error) {
        this.showMessage('error', error.message || "Erreur lors de l'envoi de l'email")
        this.resetError = error.message || "Erreur lors de l'envoi de l'email"
      } finally {
        this.isLoading = false
      }
    },
    
    async verifyResetToken(token) {
      this.isLoading = true
      
      try {
        // Récupérer l'email depuis le store ou les paramètres
        const resetEmail = this.$store.state.resetEmail || this.$route.query.email
        if (resetEmail) {
          this.resetEmail = resetEmail
        }
        
        // Valider le token côté frontend (pour l'instant)
        // Dans une vraie application, vous feriez une requête API
        setTimeout(() => {
          this.isLoading = false
          this.showMessage('success', 'Token valide. Veuillez entrer votre nouveau mot de passe.')
        }, 500)
        
      } catch (error) {
        this.showMessage('error', 'Lien de réinitialisation invalide ou expiré')
        this.resetToken = ''
        this.navigateTo('reset')
      } finally {
        this.isLoading = false
      }
    },
    
    async handlePasswordReset() {
      if (!this.validatePasswordReset()) return
      
      this.isLoading = true
      this.message = { type: '', text: '' }
      
      try {
        const resetData = {
          token: this.resetToken || 'simulated-token',
          email: this.resetEmail,
          password: this.newPassword,
          password_confirmation: this.confirmNewPassword
        }
        
        await this.resetPassword(resetData)
        this.showMessage('success', 'Mot de passe réinitialisé avec succès !')
        
        // Nettoyer les données
        this.resetToken = ''
        this.newPassword = ''
        this.confirmNewPassword = ''
        
        // Rediriger vers la connexion
        setTimeout(() => {
          this.navigateTo('login')
        }, 2000)
        
      } catch (error) {
        this.showMessage('error', error.message || 'Erreur lors de la réinitialisation')
      } finally {
        this.isLoading = false
      }
    },
    
    showMessage(type, text) {
      this.message = { type, text }
      setTimeout(() => {
        this.message = { type: '', text: '' }
      }, 5000)
    },
    
    navigateTo(tab) {
      // Sauvegarder l'état avant de naviguer
      if (tab !== this.activeTab) {
        // Nettoyer les erreurs
        this.loginErrors = {}
        this.registerErrors = {}
        this.resetError = ''
        this.passwordResetErrors = {}
        this.message = { type: '', text: '' }
        
        // Réinitialiser le formulaire de réinitialisation si on quitte cet onglet
        if (this.activeTab === 'reset-password' && tab !== 'reset-password') {
          this.resetToken = ''
          this.newPassword = ''
          this.confirmNewPassword = ''
        }
        
        // Réinitialiser le formulaire de demande de réinitialisation
        if (this.activeTab === 'reset' && tab !== 'reset') {
          this.resetSent = false
        }
      }
      
      this.activeTab = tab
      
      // Mettre à jour l'URL sans recharger la page
      this.$router.replace({ 
        query: { ...this.$route.query, tab: tab } 
      })
    },
    
    goBack() {
      // Si on vient d'une page spécifique, y retourner
      const redirectTo = this.$route.query.redirect
      if (redirectTo) {
        this.$router.push(redirectTo)
      } else {
        this.$router.go(-1)
      }
    }
  }
}
</script>